import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Film, 
  ArrowLeft, 
  ArrowRight, 
  Sparkles, 
  User, 
  Lightbulb, 
  MessageCircle, 
  CheckCircle,
  Loader2,
  PenTool,
  Layout,
  Star,
  Users,
  BookOpen,
  RefreshCw,
  Trophy,
  Clock
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Competition deadline - Fixed date: May 21, 2026 at midnight UTC
const COMPETITION_DEADLINE = new Date('2026-05-21T00:00:00Z');

const NewProject = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [enterChallenge, setEnterChallenge] = useState(false);
  
  // Countdown timer
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  
  // Calculate time remaining
  useState(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = COMPETITION_DEADLINE - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(timer);
  }, []);
  
  // Movie idea flow
  const [movieIdea, setMovieIdea] = useState('');
  const [genre, setGenre] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  
  // Project details
  const [title, setTitle] = useState('');
  const [logline, setLogline] = useState('');
  const [targetLength, setTargetLength] = useState('feature');
  
  // Story foundation (plot outline + characters)
  const [storyFoundation, setStoryFoundation] = useState(null);
  const [generatingFoundation, setGeneratingFoundation] = useState(false);

  const genres = [
    { value: 'drama', label: 'Drama' },
    { value: 'comedy', label: 'Comedy' },
    { value: 'thriller', label: 'Thriller' },
    { value: 'scifi', label: 'Sci-Fi' },
    { value: 'horror', label: 'Horror' },
    { value: 'romance', label: 'Romance' },
    { value: 'action', label: 'Action' },
    { value: 'documentary', label: 'Documentary' }
  ];

  const lengths = [
    { value: 'short', label: 'Short Film (< 40 min)' },
    { value: 'feature', label: 'Feature Film (90-120 min)' },
    { value: 'series', label: 'TV Series Episode' }
  ];

  const getAuthHeaders = () => {
    const token = localStorage.getItem('moviebot_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Process steps for onboarding
  const processSteps = [
    {
      icon: Lightbulb,
      title: "Share Your Idea",
      description: "Tell us your movie concept and genre. We'll generate professional title and logline suggestions."
    },
    {
      icon: BookOpen,
      title: "Story Foundation",
      description: "We'll create an expanded plot outline and compelling main characters for your story."
    },
    {
      icon: Layout,
      title: "3-Act Structure",
      description: "Write your screenplay scene by scene using the proven 3-act structure with guided beats."
    },
    {
      icon: MessageCircle,
      title: "Collaborative Writing",
      description: "For each scene, choose from A/B options for plot, dialogue, and actions. You're always in control."
    }
  ];

  const generateSuggestions = async () => {
    if (!movieIdea.trim() || movieIdea.length < 20) {
      toast.error('Please describe your movie idea in more detail');
      return;
    }
    if (!genre) {
      toast.error('Please select a genre first');
      return;
    }

    setGeneratingSuggestions(true);
    setSuggestions(null);
    setSelectedSuggestion(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/movie-suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({ movie_idea: movieIdea, genre: genre })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `Server error: ${response.status}`;
        throw new Error(errorMsg);
      }
      const data = await response.json();
      setSuggestions(data.suggestions);
    } catch (error) {
      console.error('Suggestions error:', error);
      const message = error?.message || 'Failed to generate suggestions';
      toast.error(String(message));
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion, index) => {
    setSelectedSuggestion(index);
    setTitle(suggestion.title);
    setLogline(suggestion.logline);
  };

  const useCustomTitleLogline = () => {
    setSelectedSuggestion('custom');
    setTitle('');
    setLogline('');
  };

  const generateStoryFoundation = async () => {
    if (!title || !logline || !genre) {
      toast.error('Please complete title, logline, and genre first');
      return;
    }

    setGeneratingFoundation(true);
    setStoryFoundation(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/story-foundation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({ 
          title, 
          logline, 
          genre,
          target_length: targetLength 
        })
      });

      if (!response.ok) throw new Error('Failed to generate story foundation');
      const data = await response.json();
      setStoryFoundation(data);
    } catch (error) {
      toast.error('Failed to generate story foundation. Please try again.');
    } finally {
      setGeneratingFoundation(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim() || !genre || !storyFoundation) {
      toast.error('Please complete all steps');
      return;
    }

    setLoading(true);

    try {
      // Create project
      const projectResponse = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          title,
          genre,
          logline,
          target_length: targetLength,
          plot_outline: storyFoundation.plot_outline,
          tone: storyFoundation.tone,
          themes: storyFoundation.themes
        })
      });

      if (!projectResponse.ok) throw new Error('Failed to create project');
      const project = await projectResponse.json();

      // Create characters from story foundation
      for (const char of storyFoundation.characters) {
        await fetch(`${API_URL}/api/projects/${project.project_id}/characters`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          
          body: JSON.stringify({
            name: char.name,
            role: char.role,
            description: char.description
          })
        });
      }

      // Enter challenge if opted in
      if (enterChallenge) {
        try {
          await fetch(`${API_URL}/api/challenge/enter`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({
              project_id: project.project_id,
              confirmations: {
                story_foundation_complete: true,
                following_moviebot: false,
                shared_challenge: false,
                original_work: true
              }
            })
          });
          toast.success('Project created & entered in the Summer Competition!');
        } catch (e) {
          toast.success('Project created! (Challenge entry may need completion in dashboard)');
        }
      } else {
        toast.success('Project created!');
      }

      navigate(`/editor/${project.project_id}`);
    } catch (error) {
      toast.error(error.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ['How It Works', 'Your Idea', 'Story Foundation', 'Review'];

  const getRoleColor = (role) => {
    switch (role) {
      case 'protagonist': return 'text-green-400 bg-green-400/20';
      case 'antagonist': return 'text-red-400 bg-red-400/20';
      default: return 'text-blue-400 bg-blue-400/20';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Film className="w-6 h-6 text-primary" />
            <span className="font-bold tracking-tight text-white">New Project</span>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="pt-20 sm:pt-24 pb-6 sm:pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6 sm:mb-8">
            {[0, 1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${
                    s === step 
                      ? 'bg-primary text-black' 
                      : s < step 
                        ? 'bg-primary/20 text-primary' 
                        : 'bg-white/5 text-muted-foreground'
                  }`}
                >
                  {s < step ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" /> : s + 1}
                </div>
                {s < 3 && (
                  <div className={`w-8 sm:w-16 h-0.5 ${s < step ? 'bg-primary' : 'bg-white/10'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <span className="text-xs sm:text-sm text-primary font-medium">{stepLabels[step]}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pb-20">
        <AnimatePresence mode="wait">
          {/* Step 0: Onboarding */}
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Welcome to MovieBot</h1>
                <p className="text-muted-foreground">Here's how we'll turn your idea into a screenplay</p>
              </div>

              <div className="space-y-4">
                {processSteps.map((processStep, index) => {
                  const Icon = processStep.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex gap-4 p-5 bg-secondary/30 border border-white/5 hover:border-primary/30 transition-colors"
                    >
                      <div className="w-12 h-12 bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-primary font-bold">STEP {index + 1}</span>
                        </div>
                        <h3 className="text-white font-bold mb-1">{processStep.title}</h3>
                        <p className="text-sm text-muted-foreground">{processStep.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="pt-6 text-center">
                <Button
                  onClick={() => setStep(1)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs px-12 py-5"
                  data-testid="start-btn"
                >
                  Let's Begin
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 1: Movie Idea + Genre */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-5"
            >
              <div className="text-center mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2">What's your movie about?</h1>
                <p className="text-sm sm:text-base text-muted-foreground">Select a genre and describe your idea</p>
              </div>

              {/* Genre Selection */}
              <div>
                <Label className="text-white text-sm font-medium">Genre</Label>
                <Select value={genre} onValueChange={(val) => { setGenre(val); setSuggestions(null); setSelectedSuggestion(null); }}>
                  <SelectTrigger className="mt-2 bg-black/50 border-white/10 text-white h-11 sm:h-12" data-testid="genre-select">
                    <SelectValue placeholder="Select a genre" />
                  </SelectTrigger>
                  <SelectContent className="bg-secondary border-white/10">
                    {genres.map((g) => (
                      <SelectItem key={g.value} value={g.value} className="text-white hover:bg-white/10">
                        {g.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Movie Idea */}
              <div>
                <Label htmlFor="movieIdea" className="text-white text-sm font-medium">Your Movie Idea</Label>
                <Textarea
                  id="movieIdea"
                  value={movieIdea}
                  onChange={(e) => setMovieIdea(e.target.value)}
                  placeholder="Describe your movie idea in a few sentences. What's the story about? Who's the main character? What do they want?

Example: A burned-out high school teacher discovers she has superpowers on the day she plans to quit. Now she must decide whether to save her failing school or finally pursue her dream of becoming a stand-up comedian."
                  className="mt-2 bg-black/50 border-white/10 focus:border-primary/50 text-white placeholder:text-white/30 min-h-[120px] sm:min-h-[160px] text-sm"
                  data-testid="movie-idea-input"
                />
              </div>

              {/* Target Length */}
              <div>
                <Label className="text-white text-sm font-medium">Target Length</Label>
                <Select value={targetLength} onValueChange={setTargetLength}>
                  <SelectTrigger className="mt-2 bg-black/50 border-white/10 text-white h-11 sm:h-12" data-testid="length-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-secondary border-white/10">
                    {lengths.map((l) => (
                      <SelectItem key={l.value} value={l.value} className="text-white hover:bg-white/10">
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={generateSuggestions}
                disabled={generatingSuggestions || movieIdea.length < 20 || !genre}
                className="w-full bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30 py-4 sm:py-5 text-sm"
                data-testid="generate-suggestions-btn"
              >
                {generatingSuggestions ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Ideas...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate Title & Logline</>
                )}
              </Button>

              {/* AI Suggestions */}
              {suggestions && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-primary">
                    <Star className="w-4 h-4" />
                    <span className="font-medium">Pick one or write your own</span>
                  </div>

                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => selectSuggestion(suggestion, index)}
                      className={`w-full p-4 text-left transition-all border-2 ${
                        selectedSuggestion === index
                          ? 'bg-primary/20 border-primary'
                          : 'bg-secondary/30 border-white/10 hover:border-primary/50'
                      }`}
                      data-testid={`suggestion-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 font-bold text-sm sm:text-base ${
                          selectedSuggestion === index ? 'bg-primary text-black' : 'bg-white/10 text-white'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-bold text-base sm:text-lg mb-1">{suggestion.title}</h3>
                          <p className="text-muted-foreground text-xs sm:text-sm line-clamp-3">{suggestion.logline}</p>
                        </div>
                        {selectedSuggestion === index && (
                          <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}

                  {/* Custom Option */}
                  <button
                    onClick={useCustomTitleLogline}
                    className={`w-full p-4 text-left transition-all border-2 ${
                      selectedSuggestion === 'custom'
                        ? 'bg-primary/20 border-primary'
                        : 'bg-secondary/30 border-white/10 hover:border-primary/50'
                    }`}
                    data-testid="custom-option"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 ${
                        selectedSuggestion === 'custom' ? 'bg-primary text-black' : 'bg-white/10 text-white'
                      }`}>
                        <PenTool className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-sm sm:text-base">Write My Own</h3>
                        <p className="text-muted-foreground text-xs sm:text-sm">Create my own title and logline</p>
                      </div>
                      {selectedSuggestion === 'custom' && (
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      )}
                    </div>
                  </button>

                  {/* Custom Input Fields */}
                  {selectedSuggestion === 'custom' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-4 p-4 bg-secondary/20 border border-white/10"
                    >
                      <div>
                        <Label className="text-white text-sm font-medium">Your Title</Label>
                        <Input
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Enter your movie title"
                          className="mt-2 bg-black/50 border-white/10 text-white h-12"
                          data-testid="custom-title-input"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-sm font-medium">Your Logline</Label>
                        <Textarea
                          value={logline}
                          onChange={(e) => setLogline(e.target.value)}
                          placeholder="A one-sentence summary of your story"
                          className="mt-2 bg-black/50 border-white/10 text-white min-h-[80px]"
                          data-testid="custom-logline-input"
                        />
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}

              <div className="flex justify-between pt-6">
                <Button
                  onClick={() => setStep(0)}
                  variant="ghost"
                  className="text-muted-foreground hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => {
                    setStep(2);
                    if (!storyFoundation) generateStoryFoundation();
                  }}
                  disabled={!title.trim() || !genre}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs px-8 py-4"
                  data-testid="step1-next-btn"
                >
                  Generate Story Foundation
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Story Foundation */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Story Foundation</h1>
                <p className="text-muted-foreground">Your expanded plot outline and main characters</p>
              </div>

              {/* Title/Logline Preview */}
              <div className="p-4 bg-primary/10 border border-primary/30">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-primary font-bold uppercase">{genre}</span>
                </div>
                <h3 className="text-white font-bold text-lg">{title}</h3>
                <p className="text-muted-foreground text-sm mt-1">{logline}</p>
              </div>

              {generatingFoundation ? (
                <div className="text-center py-16">
                  <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
                  <h3 className="text-white font-bold mb-2">Creating Your Story Foundation...</h3>
                  <p className="text-muted-foreground text-sm">Developing plot outline and characters</p>
                </div>
              ) : storyFoundation ? (
                <div className="space-y-6">
                  {/* Plot Outline */}
                  <div className="p-6 bg-secondary/30 border border-white/10">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-5 h-5 text-primary" />
                      <h3 className="text-white font-bold">Plot Outline</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-black/30 border-l-2 border-green-500">
                        <span className="text-xs text-green-400 font-bold mb-2 block">ACT 1: SETUP</span>
                        <p className="text-white/90 text-sm">{storyFoundation.plot_outline?.act_1_setup}</p>
                      </div>
                      <div className="p-4 bg-black/30 border-l-2 border-yellow-500">
                        <span className="text-xs text-yellow-400 font-bold mb-2 block">ACT 2: CONFRONTATION</span>
                        <p className="text-white/90 text-sm">{storyFoundation.plot_outline?.act_2_confrontation}</p>
                      </div>
                      <div className="p-4 bg-black/30 border-l-2 border-red-500">
                        <span className="text-xs text-red-400 font-bold mb-2 block">ACT 3: RESOLUTION</span>
                        <p className="text-white/90 text-sm">{storyFoundation.plot_outline?.act_3_resolution}</p>
                      </div>
                    </div>
                  </div>

                  {/* Characters */}
                  <div className="p-6 bg-secondary/30 border border-white/10">
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="w-5 h-5 text-primary" />
                      <h3 className="text-white font-bold">Main Characters</h3>
                    </div>
                    
                    <div className="space-y-3">
                      {storyFoundation.characters?.map((char, index) => (
                        <div key={index} className="flex gap-4 p-4 bg-black/30">
                          <div className="w-10 h-10 bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white font-bold">{char.name}</span>
                              <span className={`text-xs px-2 py-0.5 uppercase font-bold ${getRoleColor(char.role)}`}>
                                {char.role}
                              </span>
                            </div>
                            <p className="text-muted-foreground text-sm">{char.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tone & Themes */}
                  {(storyFoundation.tone || storyFoundation.themes) && (
                    <div className="p-5 bg-secondary/20 border border-white/10">
                      {storyFoundation.tone && (
                        <div className="mb-3">
                          <span className="text-xs text-primary font-bold">TONE: </span>
                          <span className="text-white/80 text-sm">{storyFoundation.tone}</span>
                        </div>
                      )}
                      {storyFoundation.themes && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-primary font-bold">THEMES: </span>
                          {storyFoundation.themes.map((theme, i) => (
                            <span key={i} className="px-2 py-1 bg-white/10 text-white/80 text-xs">{theme}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Regenerate Button */}
                  <Button
                    onClick={generateStoryFoundation}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10"
                    data-testid="regenerate-foundation-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate Story Foundation
                  </Button>
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-muted-foreground">Failed to generate. Please try again.</p>
                  <Button onClick={generateStoryFoundation} className="mt-4">
                    Try Again
                  </Button>
                </div>
              )}

              <div className="flex justify-between pt-6">
                <Button
                  onClick={() => setStep(1)}
                  variant="ghost"
                  className="text-muted-foreground hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!storyFoundation || generatingFoundation}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs px-8 py-4"
                  data-testid="step2-next-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6 sm:mb-10">
                <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-primary mx-auto mb-4" />
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2">Ready to Create</h1>
                <p className="text-muted-foreground text-sm sm:text-base">Review your project and start writing</p>
              </div>

              {/* Project Summary */}
              <div className="space-y-4 p-4 sm:p-6 bg-secondary/20 border border-white/5">
                <div className="py-3 border-b border-white/5">
                  <span className="text-muted-foreground block mb-2 text-sm">Title</span>
                  <span className="text-white font-bold text-lg sm:text-xl">{title}</span>
                </div>
                <div className="py-3 border-b border-white/5">
                  <span className="text-muted-foreground block mb-2 text-sm">Logline</span>
                  <span className="text-white text-sm sm:text-base">{logline}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/5">
                  <span className="text-muted-foreground text-sm">Genre</span>
                  <span className="text-white font-medium capitalize text-sm sm:text-base">{genre}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-muted-foreground text-sm">Length</span>
                  <span className="text-white font-medium capitalize text-sm sm:text-base">{targetLength}</span>
                </div>
              </div>

              {/* Plot Outline with Acts */}
              {storyFoundation?.plot_outline && (
                <div className="space-y-3">
                  <h3 className="text-white font-bold flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-primary" />
                    Plot Outline
                  </h3>
                  <div className="space-y-3">
                    {/* Act 1 */}
                    {storyFoundation.plot_outline.act_1_setup && (
                      <div className="border-l-4 border-green-500/50 bg-green-500/5 p-3 sm:p-4 rounded-r-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-green-500/20 text-green-400">Act 1</span>
                          <span className="text-sm font-medium text-white">Setup</span>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{storyFoundation.plot_outline.act_1_setup}</p>
                      </div>
                    )}
                    {/* Act 2 */}
                    {storyFoundation.plot_outline.act_2_confrontation && (
                      <div className="border-l-4 border-yellow-500/50 bg-yellow-500/5 p-3 sm:p-4 rounded-r-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">Act 2</span>
                          <span className="text-sm font-medium text-white">Confrontation</span>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{storyFoundation.plot_outline.act_2_confrontation}</p>
                      </div>
                    )}
                    {/* Act 3 */}
                    {storyFoundation.plot_outline.act_3_resolution && (
                      <div className="border-l-4 border-red-500/50 bg-red-500/5 p-3 sm:p-4 rounded-r-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-red-500/20 text-red-400">Act 3</span>
                          <span className="text-sm font-medium text-white">Resolution</span>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{storyFoundation.plot_outline.act_3_resolution}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Characters */}
              {storyFoundation?.characters?.length > 0 && (
                <div>
                  <h3 className="text-white font-bold flex items-center gap-2 mb-3">
                    <Users className="w-5 h-5 text-primary" />
                    Characters ({storyFoundation.characters.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {storyFoundation.characters.map((char, i) => (
                      <span key={i} className="px-3 py-1 bg-primary/20 text-primary text-sm">
                        {char.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Challenge Opt-In Box */}
              <div className="p-4 sm:p-5 bg-gradient-to-r from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-lg">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-teal-500/30 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-teal-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-bold text-base sm:text-lg mb-1">
                      This storyline has potential!
                    </h4>
                    <p className="text-teal-200/80 text-xs sm:text-sm mb-3 sm:mb-4">
                      Submit your screenplay to the <strong>Summer Film Competition</strong> — we produce the winner and submit to streaming!
                    </p>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <Checkbox 
                        checked={enterChallenge}
                        onCheckedChange={setEnterChallenge}
                        className="mt-0.5 border-teal-400 data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
                        data-testid="challenge-optin-checkbox"
                      />
                      <div>
                        <span className="text-white font-medium text-sm group-hover:text-teal-300 transition-colors">
                          Enter this project in the Summer Competition
                        </span>
                        <span className="text-teal-300/60 text-xs flex items-center gap-1 mt-1">
                          <Sparkles className="w-3 h-3" />
                          Open Now — Summer 2026
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-4 sm:pt-6">
                <Button
                  onClick={() => setStep(2)}
                  variant="ghost"
                  className="text-muted-foreground hover:text-white text-sm"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={loading}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs px-6 sm:px-8 py-4 btn-glow"
                  data-testid="create-project-btn"
                >
                  {loading ? 'Creating...' : 'Start Writing'}
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default NewProject;
